import { useTranslation } from 'react-i18next'
import { GRADES, DIMENSION_CONFIG, getGrade } from './shared/scoreUtils'

interface ScoreBreakdownProps {
  scores: {
    overall: number
    grade: string
    dimensions: Record<string, number>
    dimension_explanations?: Record<string, string>
    summary: string
  }
}

export function ScoreBreakdown({ scores }: ScoreBreakdownProps) {
  const { t } = useTranslation()
  const r = (key: string) => t(`amazonListing.scores.${key}`)
  const overallGrade = getGrade(scores.overall)

  const rows = Object.entries(DIMENSION_CONFIG)
    .filter(([key]) => key in scores.dimensions)
    .map(([key, cfg]) => {
      const score = scores.dimensions[key] ?? 0
      const grade = getGrade(score)
      return { key, score, weight: cfg.weight, weighted: Math.round(score * cfg.weight), grade }
    })

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-5">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-white mb-4">
        {r('breakdown')}
      </h3>

      {/* Dimension rows */}
      <div className="space-y-2">
        {rows.map(({ key, score, weight, weighted, grade }) => (
          <div key={key}>
            <div className="flex items-center gap-3 text-xs">
              <span className="w-16 md:w-24 text-neutral-600 dark:text-neutral-400 truncate">{r(key)}</span>
              <div className="flex-1 h-2.5 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${grade.color}`} style={{ width: `${score}%` }} />
              </div>
              <span className={`w-10 text-right font-bold ${grade.textColor}`}>{score}</span>
              <span className="w-8 text-right text-neutral-400" title={`${Math.round(weight * 100)}%`}>+{weighted}</span>
            </div>
            {scores.dimension_explanations?.[key] && (
              <p className="ml-27 text-2xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                {scores.dimension_explanations[key]}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Overall score card */}
      <div className={`mt-4 rounded-xl p-4 ${overallGrade.bgColor} ring-1 ${overallGrade.ringColor}`}>
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-xl ${overallGrade.color} flex flex-col items-center justify-center text-white flex-shrink-0`}>
            <span className="text-2xl font-bold leading-none">{scores.overall}</span>
            <span className="text-2xs opacity-80">/100</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-lg font-bold ${overallGrade.textColor}`}>
                {r(overallGrade.i18nKey)}
              </span>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {r(`gradeSummary_${overallGrade.grade}`)}
            </p>
          </div>
        </div>
      </div>

      {/* Grade scale legend (reversed: 0=bad on left, 100=good on right) */}
      <div className="mt-3 flex gap-1">
        {[...GRADES].reverse().map((g) => (
          <div
            key={g.grade}
            className={`flex-1 h-1.5 rounded-full ${g.color} ${scores.overall >= g.min && scores.overall < (GRADES[GRADES.indexOf(g) - 1]?.min ?? 101) ? 'ring-2 ring-offset-1 ring-neutral-900 dark:ring-white' : 'opacity-40'}`}
          />
        ))}
      </div>
      <div className="flex justify-between mt-1 text-2xs text-neutral-400">
        <span>0</span>
        <span>20</span>
        <span>40</span>
        <span>55</span>
        <span>70</span>
        <span>85</span>
        <span>100</span>
      </div>
    </div>
  )
}
